import {
  type JustificationRecord,
  readJustifications,
} from 'features/libra/lifecycle/lifecycleJustification'
import type { LifecycleStage } from 'features/libra/lifecycle/lifecycleTypes'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import type { OverrideJustification } from 'features/libra/overrides/overrideJustification'
import { parseZonesFromFieldValues } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  listZoneOverrides,
  type OverridableZoneField,
} from 'features/libra/stepComponents/zoneAnalysis/zoneOverrides'
import { z } from 'zod'

/**
 * One `justify.id` to surface, with the heading it renders under. The ids come
 * from the lifecycle config's action triggers — see the `justify` block in
 * `features/libra/README.md`.
 */
const justificationGroupSchema = z.object({
  id: z.string(),
  label: z.string(),
})

export const niiJustificationsConfigSchema = z.object({
  title: z.string().default('Decision Log'),
  /**
   * Lifecycle transition reasons to show, one card per `justify.id`. Zone
   * override reasons are listed separately, under `overridesTitle`.
   */
  groups: z.array(justificationGroupSchema).default([]),
  emptyText: z.string().default('No justifications recorded.'),
  overridesTitle: z.string().default('Overrides'),
})

export type NiiJustificationsConfig = z.infer<
  typeof niiJustificationsConfigSchema
>

/** A record with its stage ids resolved to stage names for display. */
export interface JustificationView extends JustificationRecord {
  fromStageName: string
  toStageName: string
}

export interface JustificationGroupView {
  id: string
  label: string
  records: JustificationView[]
}

/** Newest first, so a decision log reads top-down from the latest decision. */
export function buildJustificationGroups(
  fieldValues: FieldValues,
  groups: NiiJustificationsConfig['groups'],
  stages: ReadonlyArray<LifecycleStage>
): JustificationGroupView[] {
  const stageNames = new Map(stages.map((stage) => [stage.id, stage.name]))
  const stageName = (id: string) => stageNames.get(id) ?? id

  return groups.map(({ id, label }) => ({
    id,
    label,
    // `map` copies before `sort`, so the stored array is left alone.
    records: readJustifications(fieldValues, id)
      .map((record) => ({
        ...record,
        fromStageName: stageName(record.fromStage),
        toStageName: stageName(record.toStage),
      }))
      .sort((a, b) => Date.parse(b.justifiedAt) - Date.parse(a.justifiedAt)),
  }))
}

export interface ZoneOverrideLogEntry {
  zoneTitle: string
  field: OverridableZoneField
  justification: OverrideJustification
}

/**
 * Each zone's current, justified overrides across `OVERRIDABLE_ZONE_FIELDS`,
 * newest first.
 */
export function listJustifiedZoneOverrides(
  fieldValues: FieldValues,
  zoneFieldName?: string
): ReadonlyArray<ZoneOverrideLogEntry> {
  const { zones } = parseZonesFromFieldValues(fieldValues, zoneFieldName)
  return zones
    .flatMap((zone) =>
      listZoneOverrides(zone).flatMap(({ field, justification }) =>
        justification ? [{ zoneTitle: zone.title, field, justification }] : []
      )
    )
    .toSorted(
      (a, b) =>
        Date.parse(b.justification.justifiedAt) -
        Date.parse(a.justification.justifiedAt)
    )
}
